import { useEffect, useMemo } from 'react';
import {
  AccountDetails,
  ContractDetails,
  CustomerDetailsResponseModel,
  useGetCustomerDetailsQuery,
} from '../../store/api-generated';
import useTypedSelector from '../../helpers/useTypedSelector';
import useTypedDispatch from '../../helpers/useTypedDispatch';
import { contractSelected } from '../../store/actions/SelectedContract';
import checkCanAdjustAdvancePayment from '../../domain/checkCanAdjustAdvancePayment';

function flattenAccounts(customers: CustomerDetailsResponseModel[]) {
  return customers.flatMap((customer) => customer.accounts || []);
}

function flattenContracts(accounts: AccountDetails[]) {
  return accounts.flatMap((account) => account.contracts || []);
}

function findContractByContractId(accountId: string, id: string, contracts: ContractDetails[]) {
  return contracts.find((contract) => contract.accountId === accountId && contract.id === id) || null;
}

function findAccountByContractId(contractId: string, accounts: AccountDetails[]) {
  return accounts.find((account) => account.contracts?.some((c) => c.accountId === contractId)) || null;
}
function findCustomerByCustomerId(accountId: string, customers: CustomerDetailsResponseModel[]) {
  return customers.find((customer) => customer.accounts?.some((a) => a.customerId === accountId)) || null;
}

export default function useContract() {
  const dispatch = useTypedDispatch();

  const selectedContractState = useTypedSelector((state) => state.selectedContract);

  const { data: customerDetails, isLoading, isError } = useGetCustomerDetailsQuery({});

  const accounts = flattenAccounts(customerDetails || []);

  const contracts = flattenContracts(accounts);

  useEffect(() => {
    if (selectedContractState.status === 'NoContractSelected' && contracts?.[0]) {
      const defaultContract = contracts[0];
      dispatch(contractSelected(defaultContract.accountId!, defaultContract.id!));
    }
  }, [selectedContractState, customerDetails, dispatch]);

  const selectedContract = useMemo(() => {
    if (selectedContractState.status === 'NoContractSelected') {
      return null;
    }

    return findContractByContractId(selectedContractState.contractId, selectedContractState.id, contracts);
  }, [contracts, selectedContractState]);

  const selectedAccount = useMemo(() => {
    if (!selectedContract || !selectedContract.accountId) {
      return null;
    }

    return findAccountByContractId(selectedContract.accountId, accounts);
  }, [accounts, selectedContract]);

  const selectedCustomer = useMemo(() => {
    if (!selectedAccount || !selectedAccount.customerId) {
      return null;
    }

    return findCustomerByCustomerId(selectedAccount.customerId, customerDetails || []);
  }, [customerDetails, selectedAccount]);

  const canAdjustAdvancePayment = (selectedAccount && selectedContract) && checkCanAdjustAdvancePayment(selectedAccount, selectedContract);

  const isContractDatePassed = (() => {
    if (selectedContract?.endDate) {
      const endDate = new Date(selectedContract.endDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0); // Set time to midnight for a fair comparison

      return endDate <= today;
    }
    // If there is no endDate, the contract cannot have expired
    return false;
  })();

  return {
    selectedContract,
    selectedAccount,
    selectedCustomer,
    selectContract: (key: string) => {
      const [accountId, id] = key.split('_');
      if (accountId && id) {
        dispatch(contractSelected(accountId, id));
      }
    },
    contracts,
    canAdjustAdvancePayment,
    isLoading,
    isError,
    isContractDatePassed,
  };
}
